import { useAuth } from "@/core/auth";
import type { ReactNode } from "react";

interface RoleGuardProps {
  /** Roles that grant access. User needs at least one. */
  allowedRoles: string[];
  /** What to show when access is denied. Defaults to null (hide). */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Declarative role gate — renders children only when the current user holds
 * at least one of the `allowedRoles`. Otherwise renders `fallback` (default: nothing).
 */
export function RoleGuard({ allowedRoles, fallback = null, children }: RoleGuardProps) {
  const { roles } = useAuth();
  const hasAccess = roles.some(r => allowedRoles.includes(r));
  if (!hasAccess) return <>{fallback}</>;
  return <>{children}</>;
}

/**
 * Hook variant — returns `true` when the current user holds at least one of
 * the supplied roles. Useful for imperative checks or conditional logic
 * outside of JSX.
 */
export function useHasRole(allowedRoles: readonly string[]): boolean {
  const { roles } = useAuth();
  return roles.some(r => (allowedRoles as readonly string[]).includes(r));
}

/* ------------------------------------------------------------------ */
/*  Reusable role sets                                                 */
/* ------------------------------------------------------------------ */

/** Full admin / system-level access */
export const ADMIN_ROLES = ["admin", "enterprise_admin", "tier_sys"] as const;

/** Operator-level access (includes admins) */
export const OPERATOR_ROLES = ["admin", "enterprise_admin", "tier_sys", "ops_operator", "operator"] as const;

/** Member-level access (includes operators and admins) */
export const MEMBER_ROLES = ["admin", "enterprise_admin", "tier_sys", "ops_operator", "operator", "member"] as const;
